// src/invoices/articles.js — екран „Артикули" (ПРОМТ-7 §7).
//
// Артикулът вече не е само „име + цена" за фактурата: има код, тип, група,
// базова мярка с преобразувания и счетоводен слой (сметки и кодове за Конто).
// Бекендът (routes/articles.js, routes/article-groups.js, services/articles.js)
// пази правилата; тук е само показването и формите.

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, searchInput, loadError } from '../shared/ui.js';
import { regHead, regFilters, regField, regTools, regCount, cutCell, REG_TABLE } from '../shared/register.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state, cq, activeCompanyObj } from '../shared/state.js';
import kontoCfg from '../../services/kontoConfig.js';
import { effectiveAccounts } from '../../services/articles.js';
import { stockTypes } from '../../services/stock.js';
import { activeRecipe, recipeLabel } from '../warehouse/recipes.js';
import { openRecipes } from '../warehouse/index.js';
import { uploadRaw } from '../shared/upload.js';
import { dateInput, localTodayIso, isoToBg } from '../shared/dates.js';
import { plural, amount } from '../shared/format.js';
import { kontoExportDialog } from '../shared/kontoExport.js';
import { priceListsDialog } from './priceLists.js';
import { bulkManager } from '../shared/pickers.js';
import { parsePack, isMetric as packIsMetric } from '../../services/packs.js';
import { remeasureDialog } from '../shared/remeasure.js';
import { withXlsx } from '../shared/xlsx.js';

// Предложението за „Смени мярката" от името („барбекю сос туб 2,2кг").
function remeasureSuggest(a) {
  const kg = parsePack(a && a.name, 'кг');
  if (kg) return { measure: 'кг', factor: kg };
  const l = parsePack(a && a.name, 'л');
  return l ? { measure: 'л', factor: l } : null;
}

export const TYPE_LABELS = { goods: 'Стока', service: 'Услуга', material: 'Материал', dish: 'Ястие' };
const TYPES = Object.keys(TYPE_LABELS);
const ACCOUNT_FIELDS = [
  ['stock_account', 'Сметка склад'],
  ['revenue_account', 'Сметка приход'],
  ['cogs_account', 'Сметка себестойност'],
];

// Търсенето и отметката се помнят между пречертаванията (след запис екранът
// се рисува наново — човек не бива да губи филтъра си всеки път).
let _q = '';
let _showInactive = false;
// Филтрите по вид и група (Макс, 20.10.2026) — помнят се при пречертаване,
// както търсачката. '' = всички; '_none' = „Без група".
let _type = '';
let _group = '';

const role = () => (state().user && state().user.role) || '';
const isAdmin = () => role() === 'admin';
// Счетоводният слой (сметки, кодове за Конто) е работа на кантората. Клиентът
// само издава фактури — полетата там биха го объркали и той не бива да ги
// пипа, затова изобщо не ги вижда (и не ги праща при запис).
const isStaff = () => role() === 'admin' || role() === 'user';
const hasWarehouse = () => { const m = (activeCompanyObj() || {}).warehouse_mode; return m === 'trade' || m === 'materials'; };

const reload = () => view($('#page'));
const hasCode = (a) => a && a.code != null && String(a.code).trim() !== '';
const matches = (a, q) => !q || [a.code, a.name, a.barcode].some((v) => String(v == null ? '' : v).toLowerCase().includes(q));

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  let arts;
  try { arts = await api('/articles' + cq()); }
  catch (e) { page.appendChild(loadError(() => view(page), 'Артикулите не можаха да се заредят. Проверете връзката.')); return; }
  if (!Array.isArray(arts)) arts = [];
  // Групите са само за показване на името и за падащия списък — ако не дойдат,
  // екранът пак работи (артикулите са по-важни от имената на групите).
  let groups = [];
  try { groups = await api('/article-groups' + cq()); if (!Array.isArray(groups)) groups = []; } catch (e) { groups = []; }
  const groupName = {};
  groups.forEach((g) => { groupName[g.id] = g.name; });
  // Колона „Наличност" (ПРОМТ-8) — само при фирма със склад и само ако
  // наличностите дойдат (без право „Склад" колоната просто я няма).
  const whTypes = stockTypes((activeCompanyObj() || {}).warehouse_mode);
  let stockOf = null;
  if (whTypes.length) {
    try {
      const rows = await api('/stock/balances' + cq());
      stockOf = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => { stockOf[r.article_id] = r; });
    } catch (e) { stockOf = null; }
  }
  // Рецептурите (ПРОМТ-13) — само при ресторант и само ако има ястия.
  // Без право „Склад" или при друга фирма сървърът връща 403 — тогава просто
  // не показваме нищо до ястията (recipes остава null).
  let recipes = null;
  if ((activeCompanyObj() || {}).warehouse_mode === 'materials' && arts.some((a) => a.type === 'dish')) {
    try { const r = await api('/recipes' + cq()); recipes = Array.isArray(r) ? r : null; } catch (e) { recipes = null; }
  }
  // Бутон в клетката на името: отваря склада на рецептурата на ястието.
  // stopPropagation — иначе кликът стига и до реда и отваря формата.
  const recipeLink = (a) => {
    if (!recipes || a.type !== 'dish') return null;
    const r = activeRecipe(recipes, a.id);
    return el('button', { class: 'btn sm ghost article-recipe', type: 'button', style: 'margin-left:8px',
      text: r ? 'Рецептура: ' + recipeLabel(r) : 'Няма рецептура',
      'aria-label': (r ? 'Рецептура ' + recipeLabel(r) : 'Няма рецептура') + ' — отвори рецептурите на ' + (a.name || 'ястието'),
      onClick: (e) => { e.stopPropagation(); openRecipes(a.id); } });
  };
  const nameCell = (a) => {
    const link = recipeLink(a);
    if (!link) return cutCell(a.name || '', { style: 'font-weight:600' });
    return el('td', { style: 'font-weight:600' }, a.name || '', link);
  };
  const stockCell = (a) => {
    if (!whTypes.includes(a.type)) return el('td', { class: 'num', text: '—' });
    const b = stockOf[a.id];
    const q = b ? Math.round(Number(b.qty) * 10000) / 10000 : 0;
    return el('td', { class: 'num' + (q < 0 ? ' stock-neg-text' : '') }, String(q) + ' ' + (a.measure || ''));
  };

  const panel = el('div', { class: 'panel' });
  // Избор на много редове и масова смяна — само за администратора (както
  // масовото триене навсякъде другаде). Сървърът пази същото: /articles/bulk.
  // Създава се тук, защото лентата му стои в заглавния ред (като „Фактури").
  const bulk = bulkManager({ endpoint: '/articles', refresh: reload, label: 'артикула', actions: [
    { label: 'Смени групата…', run: (ids) => bulkSetDialog(ids, 'Смени групата', 'Нова група', 'group_id',
      [['', 'Без група']].concat(groups.map((g) => [String(g.id), g.name]))) },
    { label: 'Смени вида…', run: (ids) => bulkSetDialog(ids, 'Смени вида', 'Нов вид', 'type', TYPES.map((t) => [t, TYPE_LABELS[t]])) },
    { label: 'Активни / неактивни…', run: (ids) => bulkSetDialog(ids, 'Активни или неактивни', 'Да бъдат', 'active', [['true', 'Активни'], ['false', 'Неактивни (не се предлагат във фактурите)']]) },
  ] });
  // Видът е като в „Доставки" (Макс, 29.10.2026): заглавието вляво, всички
  // бутони вдясно на един ред, „+ Нов артикул" — последен. Бутоните са най-много
  // четири, затова няма „Още ▾" — всичко се вижда с едно натискане.
  // „Внос от Excel" и ценовите листи (т. 5 a) са за всички — образецът е
  // направен за клиента. Групите (сметки за Конто) са работа на кантората, а
  // номенклатурата за Конто (ПРОМТ-11) — само при склад: без склад в Конто
  // няма аналитичност по артикул.
  const head = regHead('Артикули', [
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Ценови листи', onClick: () => priceListsDialog() }),
    el('button', { class: 'btn sm ghost', type: 'button', text: 'Внос от Excel', onClick: () => importDialog() }),
    isStaff() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Групи', onClick: () => groupsDialog() }) : null,
    isStaff() && hasWarehouse() ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Експорт за Конто', onClick: () => kontoArticlesDialog() }) : null,
  ], el('button', { class: 'btn sm', type: 'button', text: '+ Нов артикул', onClick: () => articleForm(null, groups) }),
  bulk.toolbar.length ? [el('div', { class: 'bulk-bar' }, ...bulk.toolbar)] : []);
  panel.appendChild(head);

  // Заварените артикули (отпреди ПРОМТ-7) нямат код; складът ги третира като
  // неактивни. Лентата стои, докато миграцията не им даде кодове.
  const noCode = arts.filter((a) => !hasCode(a)).length;
  if (noCode) {
    panel.appendChild(el('div', { class: 'sev-warn articles-nocode', role: 'status',
      text: noCode + ' ' + (noCode === 1 ? 'артикул' : 'артикула') + ' без код — пуснете миграцията: node scripts/migrate-article-codes.js' }));
  }

  if (!arts.length) {
    panel.appendChild(emptyState('⛁', 'Още няма запаметени артикули.', '+ Нов артикул', () => articleForm(null, groups)));
    page.appendChild(panel);
    return;
  }

  // Търсачката е в лентата с филтрите (като в „Доставки"), със запомнения текст.
  const search = searchInput({ placeholder: 'Търсене…', ariaLabel: 'Търсене в „Артикули"', value: _q, onSearch: (q) => { _q = q; draw(); } });
  const inactiveCount = arts.filter((a) => a.active === false).length;
  const cb = el('input', { type: 'checkbox', checked: _showInactive, onChange: (e) => { _showInactive = !!e.target.checked; draw(); } });
  const inactiveLbl = el('label', { style: 'display:inline-flex;gap:6px;align-items:center;cursor:pointer' }, cb,
    'Покажи неактивните' + (inactiveCount ? ' (' + inactiveCount + ')' : ''));
  // Вид и група: при стотици артикули (вносът от Конто) иначе не се намира
  // нищо. Групата е на кантората — клиентът не я вижда никъде, нито тук.
  // Надписът стои над полето (regField) — затова без отделен aria-label.
  const usedTypes = TYPES.filter((t) => arts.some((a) => (a.type || 'service') === t));
  const typeSel = el('select', { onChange: (e) => { _type = e.target.value; draw(); } },
    el('option', { value: '', text: 'Всички видове' }), ...usedTypes.map((t) => el('option', { value: t, text: TYPE_LABELS[t] })));
  typeSel.value = usedTypes.includes(_type) ? _type : '';
  _type = typeSel.value;
  let groupSel = null;
  if (isStaff()) {
    groupSel = el('select', { onChange: (e) => { _group = e.target.value; draw(); } },
      el('option', { value: '', text: 'Всички групи' }), el('option', { value: '_none', text: 'Без група' }),
      ...groups.map((g) => el('option', { value: String(g.id), text: g.name })));
    groupSel.value = (_group === '_none' || groups.some((g) => String(g.id) === _group)) ? _group : '';
    _group = groupSel.value;
  } else { _group = ''; }
  // Отметката и „Маркирай всички като стоки" са на отделен ред под филтрите.
  panel.appendChild(regFilters(
    regField('Код, име или баркод', search, 'l'),
    regField('Вид', typeSel),
    groupSel ? regField('Група', groupSel) : null,
    regTools(inactiveLbl, warehouseAction(arts))));
  const countLbl = regCount();
  panel.appendChild(countLbl);

  const tb = el('tbody');
  const emptyNote = el('div', { class: 'filter-empty', role: 'status', style: 'padding:14px 4px;color:var(--muted)' });
  const sorted = arts.slice().sort((a, b) => {
    // Без код — накрая; кодовете с водещи нули се сравняват като текст с
    // числов режим, за да не излезе „10" преди „9" при ръчни кодове.
    if (hasCode(a) !== hasCode(b)) return hasCode(a) ? -1 : 1;
    const c = String(a.code || '').localeCompare(String(b.code || ''), 'bg', { numeric: true });
    return c || String(a.name || '').localeCompare(String(b.name || ''), 'bg');
  });

  function draw() {
    clear(tb);
    bulk.reset();
    const list = sorted.filter((a) => (_showInactive || a.active !== false) && matches(a, _q)
      && (!_type || (a.type || 'service') === _type)
      && (!_group || (_group === '_none' ? a.group_id == null : String(a.group_id) === _group)));
    countLbl.set(list.length, arts.length, _q);
    list.forEach((a) => {
      const inactive = a.active === false;
      // clickableRow: редът се отваря и с клавиатура (правилото от CLAUDE.md —
      // onClick на <tr> веднъж направи цял списък недостъпен).
      const tr = clickableRow(() => articleForm(a, groups), 'Отвори ' + (a.name || 'артикул') + (inactive ? ' (неактивен)' : ''),
        bulk.cell(a.id, a.name || 'артикул'),
        el('td', { style: 'font-variant-numeric:tabular-nums' }, hasCode(a) ? String(a.code) : el('span', { class: 'tag amber', text: 'без код' })),
        // Дългото име е на един ред с „…" (целият текст — при задържане). При
        // ястие с бутон за рецептурата клетката не се реже, иначе бутонът изчезва.
        nameCell(a),
        el('td', {}, TYPE_LABELS[a.type] || TYPE_LABELS.service),
        el('td', {}, (a.group_id != null && groupName[a.group_id]) || '—'),
        el('td', {}, a.measure || ''),
        // Със запетая, както „Склад" (ПРОМТ-19, т. 5); Excel/Конто четат данните, не клетката.
        el('td', { class: 'num' }, amount(a.unit_price)),
        stockOf ? stockCell(a) : null,
        el('td', {}, inactive ? 'не' : 'да'));
      if (inactive) { tr.classList.add('row-inactive'); tr.style.opacity = '0.55'; }
      tb.appendChild(tr);
    });
    emptyNote.textContent = list.length ? '' : ((_q || _type || _group) ? 'Нищо не е намерено по тези филтри.' : 'Всички артикули са неактивни — отметнете „Покажи неактивните".');
    emptyNote.style.display = list.length ? 'none' : '';
  }

  panel.appendChild(el('table', { class: REG_TABLE + ' articles-table' },
    el('thead', {}, el('tr', {},
      bulk.headTh,
      el('th', { text: 'Код' }), el('th', { text: 'Наименование' }), el('th', { text: 'Тип' }), el('th', { text: 'Група' }),
      el('th', { text: 'Мярка' }), el('th', { class: 'num', text: 'Цена' }),
      stockOf ? el('th', { class: 'num', text: 'Наличност' }) : null,
      el('th', { text: 'Активен' }))),
    tb));
  panel.appendChild(emptyNote);
  draw();
  page.appendChild(panel);
}

// Масова смяна на едно поле на избраните артикули — един прозорец, един
// запис на сървъра (/articles/bulk, в една транзакция).
function bulkSetDialog(ids, title, label, key, options) {
  const sel = el('select', {}, ...options.map(([v, t]) => el('option', { value: v, text: t })));
  const ok = el('button', { class: 'btn', type: 'button', text: 'Смени', style: 'margin-left:8px', onClick: async () => {
    const raw = sel.value;
    const value = key === 'group_id' ? (raw === '' ? null : Number(raw)) : key === 'active' ? raw === 'true' : raw;
    try {
      const r = await api('/articles/bulk', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, ids, set: { [key]: value } }) });
      closeModal();
      const skipped = Array.isArray(r.skipped) ? r.skipped : [];
      toast('Сменени: ' + r.updated + (r.unchanged ? ' · вече бяха така: ' + r.unchanged : '') + '.');
      if (skipped.length) toast('Не са сменени ' + skipped.length + ': ' + skipped.slice(0, 5).map((x) => '„' + x.name + '" — ' + x.reason).join('; ') + (skipped.length > 5 ? '…' : ''), true);
      reload();
    } catch (e) { toast(e.message, true); }
  } });
  openModal({ title: title + ' — ' + plural(ids.length, 'артикул', 'артикула'), narrow: true,
    body: el('div', {}, field(label, sel)),
    foot: el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ok) });
}

// „Маркирай всички като стоки" — само за фирма с включен склад.
// КОИ: активни артикули с тип „Услуга" и БЕЗ група. Защо така: до ПРОМТ-7
// артикулът нямаше тип и сървърът дава „Услуга" по подразбиране — тоест
// „услуга без група" е точно заварено, некласифицирано състояние. Артикул в
// група или с друг тип някой вече е подредил съзнателно и не го пипаме;
// неактивните не отиват в склада, затова и тях не пипаме.
function warehouseAction(arts) {
  const c = activeCompanyObj();
  // Складът следва функцията (warehouse_mode), както сървърът и менюто.
  if (!c || (c.warehouse_mode !== 'trade' && c.warehouse_mode !== 'materials')) return null;
  const cands = arts.filter((a) => a.active !== false && (a.type || 'service') === 'service' && a.group_id == null);
  if (!cands.length) return null;
  return el('button', { class: 'btn sm ghost', type: 'button', text: 'Маркирай всички като стоки', onClick: async () => {
    const ok = await confirmDialog('Да маркирам ли ' + cands.length + ' ' + (cands.length === 1 ? 'артикул' : 'артикула')
      + ' с тип „Услуга" и без група като „Стока"? Истинските услуги (напр. „Счетоводно обслужване") после върнете на ръка.',
    { okText: 'Маркирай като стоки' });
    if (!ok) return;
    let done = 0, failed = 0;
    // Един по един, не наведнъж — при грешка по средата знаем колко са минали.
    for (const a of cands) {
      try { await api('/articles/' + a.id, { method: 'PUT', body: JSON.stringify({ type: 'goods' }) }); done++; }
      catch (e) { failed++; }
    }
    if (failed) toast('Маркирани: ' + done + '. Неуспешни: ' + failed + '.', true);
    else toast('Маркирани като стоки: ' + done + '.');
    reload();
  } });
}

// Подсказката „празно = …" за сметка: какво ще важи, ако полето остане празно.
function accountHint(key, type, group) {
  const eff = effectiveAccounts({ type }, group || null, kontoCfg.DEFAULTS)[key];
  const val = eff == null || eff === '' ? 'без сметка' : eff;
  return group ? 'празно = от групата: ' + val : 'празно = по типа „' + (TYPE_LABELS[type] || '') + '": ' + val;
}

let hintSeq = 0;
// Поле с подсказка отдолу, вързана с aria-describedby — четецът я прочита
// заедно с етикета, а не като отделен, откъснат текст.
function hinted(label, inp, hintText) {
  const id = 'art-hint-' + (++hintSeq);
  inp.setAttribute('aria-describedby', id);
  const hint = el('div', { id, class: 'field-hint', style: 'font-size:var(--fs-sm);color:var(--muted);margin-top:4px', text: hintText || '' });
  const f = field(label, inp);
  f.appendChild(hint);
  return { field: f, hint };
}

function articleForm(existing, groups) {
  const a = existing || {};
  const k6 = (a.accounting && a.accounting.konto6) || {};
  const activeGroups = (groups || []).filter((g) => g.active !== false || g.id === a.group_id);

  const f = {
    code: input({ value: a.code || '', maxlength: '30', inputmode: 'numeric', autocomplete: 'off' }),
    name: input({ value: a.name || '' }),
    type: el('select', {}, ...TYPES.map((t) => el('option', { value: t, text: TYPE_LABELS[t], selected: (a.type || (existing ? 'service' : defaultType())) === t }))),
    group: el('select', {}, el('option', { value: '', text: '— без група —' }),
      ...activeGroups.map((g) => el('option', { value: g.id, text: g.name, selected: a.group_id != null && Number(a.group_id) === Number(g.id) }))),
    measure: input({ value: a.measure || 'бр.', maxlength: '30' }),
    unit_price: input({ type: 'number', step: '0.01', value: a.unit_price != null ? a.unit_price : 0 }),
    vat_rate: input({ type: 'number', step: '1', min: '0', max: '100', value: a.vat_rate != null ? a.vat_rate : 20 }),
    barcode: input({ value: a.barcode || '', maxlength: '40', autocomplete: 'off' }),
    min_qty: input({ type: 'number', step: '0.001', min: '0', value: a.min_qty != null ? a.min_qty : 0 }),
  };

  const nextBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Следващ свободен', onClick: async () => {
    try {
      const r = await api('/articles/next-code' + cq());
      if (r && r.code) {
        f.code.value = r.code;
        // Като писане на ръка — формата става „пипната" и Esc пита.
        f.code.dispatchEvent(new window.Event('input', { bubbles: true }));
      }
    } catch (e) { toast(e.message, true); }
  } });
  const codeWrap = el('div', { style: 'display:flex;gap:8px;align-items:center' }, f.code, nextBtn);

  // ---- Преобразувания: 1 единица = N базови ----
  const convs = (Array.isArray(a.conversions) ? a.conversions : []).map((c) => ({ unit: c.unit || '', factor: c.factor != null ? c.factor : '' }));
  const convBody = el('tbody');
  const baseLbl = () => (f.measure.value || '').trim() || 'базова';
  function drawConvs() {
    clear(convBody);
    if (!convs.length) {
      convBody.appendChild(el('tr', {}, el('td', { colspan: '3', style: 'color:var(--muted)', text: 'Няма — артикулът се води само в „' + baseLbl() + '".' })));
      return;
    }
    convs.forEach((c, idx) => {
      // Полетата в реда нямат <label> — номерът на реда влиза в името, за да
      // различава четецът редовете (както при редовете на фактурата).
      const rowNo = 'ред ' + (idx + 1);
      const unit = input({ value: c.unit, placeholder: 'кашон', 'aria-label': 'Единица, ' + rowNo, onInput: (e) => { c.unit = e.target.value; } });
      const factor = input({ type: 'number', step: '0.001', min: '0', value: c.factor, placeholder: '24', 'aria-label': 'Колко ' + baseLbl() + ' е 1 единица, ' + rowNo, onInput: (e) => { c.factor = e.target.value; } });
      convBody.appendChild(el('tr', {},
        el('td', {}, unit),
        el('td', { class: 'num', style: 'width:160px' }, factor),
        el('td', { style: 'width:40px' }, el('button', { class: 'btn sm danger', type: 'button', text: '×', 'aria-label': 'Премахни преобразуване, ' + rowNo,
          onClick: () => { convs.splice(idx, 1); drawConvs(); } }))));
    });
  }
  f.measure.addEventListener('input', drawConvs);
  drawConvs();
  const convBox = el('div', { class: 'articles-convs', style: 'margin:6px 0 12px' },
    el('h4', { text: 'Преобразувания', style: 'margin:10px 0 4px' }),
    el('p', { style: 'margin:0 0 6px;font-size:var(--fs-sm);color:var(--muted)', text: 'Например: 1 кашон = 24 бр. Базовата мярка е тази, в която се води складът.' }),
    el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Единица' }), el('th', { class: 'num', text: 'Колко базови единици' }), el('th', {}))),
      convBody),
    el('button', { class: 'btn sm ghost', type: 'button', text: '+ ред', style: 'margin-top:6px',
      onClick: () => { convs.push({ unit: '', factor: '' }); drawConvs(); const ins = convBody.querySelectorAll('input'); if (ins.length) ins[ins.length - 2].focus(); } }));

  const body = el('div', {},
    row(field('Код', codeWrap), field('Наименование', f.name)),
    row(field('Тип', f.type), field('Група', f.group)),
    row(field('Базова мярка', f.measure, { size: 'm' }), field('Цена', f.unit_price, { size: 'm' }), field('ДДС %', f.vat_rate, { size: 's' })),
    // Мярката на артикул с наличност не се пише на ръка (16 бр. биха станали
    // 16 кг) — бутонът я сменя и преизчислява склада (services/remeasure.js).
    existing && existing.id && isAdmin() && !packIsMetric(a.measure)
      ? el('div', { style: 'margin:-4px 0 8px' }, el('button', { class: 'btn sm ghost', type: 'button', text: 'Смени мярката',
        onClick: () => remeasureDialog(existing, remeasureSuggest(existing), () => { closeModal(); reload(); }) }),
        el('span', { class: 'cal-hint', style: 'margin-left:8px', text: 'напр. спагети от бройки в кг — с преизчисляване на наличността' }))
      : null,
    row(field('Баркод', f.barcode), field('Минимална наличност', f.min_qty, { size: 'm' })),
    convBox);

  // ---- За счетоводителя (само admin/user) ----
  let acc = null;
  if (isStaff()) {
    acc = {
      code: input({ value: k6.code || '', maxlength: '30' }),
      measure: input({ value: k6.measure || '', maxlength: '10' }),
      article_type: input({ type: 'number', step: '1', value: k6.article_type != null ? k6.article_type : '' }),
    };
    const kCode = hinted('Код за Конто', acc.code, 'празно = кодът на артикула');
    const kMeasure = hinted('Мярка за Конто', acc.measure, 'празно = ' + ((kontoCfg.DEFAULTS.measure_codes || {})[a.measure || 'бр.'] || kontoCfg.DEFAULTS.measure || '001'));
    const kType = hinted('Тип артикул за Конто', acc.article_type, '');
    const accRows = ACCOUNT_FIELDS.map(([key, label]) => {
      acc[key] = input({ value: a[key] || '', maxlength: '20', inputmode: 'numeric' });
      return Object.assign(hinted(label, acc[key], ''), { key });
    });
    const selectedGroup = () => (groups || []).find((g) => String(g.id) === String(f.group.value)) || null;
    const refreshHints = () => {
      const g = selectedGroup();
      accRows.forEach((r) => { r.hint.textContent = accountHint(r.key, f.type.value, g); });
      const t = g && g.konto_article_type != null && g.konto_article_type !== '' ? g.konto_article_type : kontoCfg.DEFAULTS.article_type;
      kType.hint.textContent = (g ? 'празно = от групата: ' : 'празно = ') + t;
    };
    f.type.addEventListener('change', refreshHints);
    f.group.addEventListener('change', refreshHints);
    refreshHints();
    const details = el('details', { class: 'articles-accounting', style: 'border:1px solid var(--border);border-radius:var(--radius-sm);padding:6px 14px;margin:6px 0' },
      el('summary', { text: 'За счетоводителя', style: 'cursor:pointer;font-weight:700;padding:6px 0' }),
      row(kCode.field, kMeasure.field, kType.field),
      row(...accRows.map((r) => r.field)));
    // Нещо вече е попълнено ръчно → разгъваме, за да не остане невидимо.
    if (k6.code || k6.measure || k6.article_type != null || ACCOUNT_FIELDS.some(([k]) => a[k])) details.open = true;
    body.appendChild(details);
  }

  let active = null;
  if (existing && existing.id) {
    active = el('input', { type: 'checkbox', checked: a.active !== false });
    body.appendChild(el('label', { style: 'display:inline-flex;gap:6px;align-items:center;margin-top:8px;cursor:pointer' }, active,
      'Активен (неактивният не се предлага във фактурите и склада)'));
  }

  const save = async () => {
    const p = {
      company_id: state().activeCompany,
      code: f.code.value.trim(),
      name: f.name.value.trim(),
      type: f.type.value,
      group_id: f.group.value ? Number(f.group.value) : null,
      measure: f.measure.value.trim() || 'бр.',
      unit_price: f.unit_price.value,
      vat_rate: f.vat_rate.value === '' ? 20 : Number(f.vat_rate.value),
      barcode: f.barcode.value.trim(),
      min_qty: f.min_qty.value === '' ? 0 : Number(f.min_qty.value),
      // Изцяло празният ред е „+ ред", натиснат по невнимание — не е грешка.
      // Наполовина попълненият отива на сървъра и той казва какво не е наред.
      conversions: convs.filter((c) => String(c.unit).trim() !== '' || String(c.factor).trim() !== '')
        .map((c) => ({ unit: String(c.unit).trim(), factor: c.factor === '' ? 0 : Number(c.factor) })),
    };
    if (active) p.active = !!active.checked;
    // Клиентът не праща счетоводните полета изобщо — частичният PUT на
    // сървъра пази каквото е въвела кантората.
    if (acc) {
      p.accounting = { konto6: { code: acc.code.value.trim(), measure: acc.measure.value.trim(), article_type: acc.article_type.value === '' ? null : Number(acc.article_type.value) } };
      ACCOUNT_FIELDS.forEach(([k]) => { p[k] = acc[k].value.trim() || null; });
    }
    if (!p.name) { toast('Въведете наименование.', true); f.name.focus(); return; }
    try {
      if (existing && existing.id) await api('/articles/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/articles', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано.');
      closeModal();
      reload();
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', {});
  if (existing && existing.id && isAdmin()) {
    foot.appendChild(el('button', { class: 'btn sm danger', type: 'button', text: 'Изтрий', style: 'margin-right:auto', onClick: () => deleteArticle(existing) }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: (existing && existing.id) ? 'Редакция на артикул' : 'Нов артикул', body, foot, wide: true });
}

// Типът по подразбиране за НОВ артикул: фирма със склад продава предимно
// стоки/материали, фирма без склад — услуги (кантората, консултанти).
function defaultType() {
  const c = activeCompanyObj();
  if (c && (c.warehouse_mode === 'trade' || c.warehouse_mode === 'materials')) return c.warehouse_mode === 'materials' ? 'material' : 'goods';
  return 'service';
}

async function deleteArticle(a) {
  const ok = await confirmDialog('Да изтрия ли артикула „' + (a.name || '') + '"?', { okText: 'Изтрий', danger: true });
  if (!ok) return;
  try {
    await api('/articles/' + a.id, { method: 'DELETE' });
    toast('Изтрито.');
    closeModal();
    reload();
  } catch (e) {
    toast(e.message, true);
    // 409 = артикулът е в ред на фактура/проформа. Изтриването би скъсало
    // следата към документа, затова предлагаме деактивиране — артикулът
    // остава за историята, но не се предлага повече.
    if (e.status !== 409) return;
    const deact = await confirmDialog(e.message + ' Да го деактивирам ли?', { okText: 'Деактивирай' });
    if (!deact) return;
    try {
      await api('/articles/' + a.id, { method: 'PUT', body: JSON.stringify({ active: false }) });
      toast('Артикулът е деактивиран.');
      closeModal();
      reload();
    } catch (e2) { toast(e2.message, true); }
  }
}

// ---- Групи ----
// Групата носи сметките и типа за Конто; артикулите без свои сметки ги
// наследяват. Затова групите са в отделен прозорец, а не в реда на артикула.
// ---- „Експорт за Конто" (ПРОМТ-11): номенклатурата ----
// По подразбиране само новите/променените след последния файл — за да не се
// внася всеки месец целият списък наново.
function kontoArticlesDialog() {
  const allBox = el('input', { type: 'checkbox' });
  return kontoExportDialog({
    kind: 'articles', title: 'Експорт за Конто — артикули', url: '/articles/konto-export',
    intro: 'Складовите артикули с код — за „Импорт на номенклатури от Excel" в Конто 6.',
    controls: [el('label', { style: 'display:flex;gap:8px;align-items:center;margin:6px 0' }, allBox, el('span', { text: 'Всички артикули (не само новите и променените)' }))],
    payload: () => ({ scope: allBox.checked ? 'all' : 'changed' }),
    headline: (r) => plural(r.count, 'артикул', 'артикула') + ' за Конто',
    sub: (r) => (r.since ? 'Нови или променени след последния файл.' : 'Всички складови артикули.'),
  });
}

// ---- „Внос от Excel" (ПРОМТ-16): ЕДИН бутон за двата формата ----
// Образецът „артикули и наличности" (колона „Вид") → новият внос с
// начални наличности; справката от номенклатурата на Конто → вносът от
// ПРОМТ-10в. Кой е файлът, се познава по заглавията — човекът не избира.
const isTemplateHead = (cells) => (cells || []).some((c) => String(c == null ? '' : c).toLowerCase().replace(/\([^)]*\)/g, '').replace(/[*\s.,:%_\-–—]/g, '') === 'вид');

export function importDialog() {
  const c = activeCompanyObj() || {};
  const stockOn = c.warehouse_mode === 'trade' || c.warehouse_mode === 'materials';
  const date = dateInput(localTodayIso());
  const out = el('div', { class: 'imp-out', role: 'status', 'aria-live': 'polite' });
  const foot = el('div', {});
  let file = null;

  const fileInput = el('input', { type: 'file', accept: '.xlsx,.xls', class: 'sr-only', id: 'imp-file-' + Date.now() });
  const fileName = el('span', { class: 'muted', text: 'Още не е избран файл.' });
  fileInput.addEventListener('change', async () => {
    const f = fileInput.files && fileInput.files[0];
    fileInput.value = '';
    if (!f) return;
    file = f;
    fileName.textContent = f.name;
    let head = [];
    try {
      const buf = await f.arrayBuffer();
      const wb = await withXlsx(() => XLSX.read(buf, { type: 'array' }));
      const name = wb.SheetNames.includes('Артикули') ? 'Артикули' : wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' });
      head = rows.find((r) => r.some((x) => String(x).trim() !== '')) || [];
    } catch (e) { showError('Файлът не можа да се прочете. Запишете го отново като Excel (.xlsx).'); return; }
    if (isTemplateHead(head)) { await templatePreview(); return; }
    // Справката от Конто — вносът от ПРОМТ-10в, в същия прозорец.
    if (!isStaff()) { showError('Това не е образецът — свалете го от връзката горе и попълнете него.'); return; }
    kontoImport(f, out, foot);
  });

  function showError(text) {
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'sev-warn', role: 'alert', text }));
    foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal }));
  }

  const url = (mode) => '/api/articles/import-template' + cq() + '&mode=' + mode + (stockOn && date.dataset.iso ? '&date=' + encodeURIComponent(date.dataset.iso) : '');

  async function templatePreview() {
    if (!file) return;
    clear(out); clear(foot);
    out.appendChild(el('p', { class: 'muted', text: 'Проверявам таблицата…' }));
    let r;
    try { r = await uploadRaw(url('preview'), file, { contentType: 'application/octet-stream' }); }
    catch (e) { showError(e.message); return; }
    renderPreview(r, false);
  }

  function renderPreview(r, done) {
    clear(out); clear(foot);
    const t = r.totals || {};
    const count = t.created + t.updated;
    if (r.already_uploaded_at && !done) out.appendChild(el('p', { class: 'sev-warn', text: 'Тази таблица вече е качвана на ' + isoToBg(r.already_uploaded_at) + '.' }));
    // Едно изречение: колко, колко нови/обновени, колко с наличност и за колко.
    const parts = [];
    if (count) parts.push((done ? 'Внесени са ' : 'Ще се внесат ') + plural(count, 'артикул', 'артикула') + ' — ' + t.created + ' нови, ' + t.updated + ' обновени.');
    if (t.with_stock) parts.push(t.with_stock + ' ' + (done ? (t.with_stock === 1 ? 'получи' : 'получиха') : (t.with_stock === 1 ? 'получава' : 'получават'))
      + ' начална наличност на стойност ' + amount(t.value) + ' €' + (r.date ? ' към ' + isoToBg(r.date) : '') + '.');
    if (!parts.length) parts.push('Няма нищо ново за внасяне.');
    out.appendChild(el('p', { class: 'imp-lead', text: parts.join(' ') }));
    if (!done && (r.errors || []).length) {
      out.appendChild(el('p', { class: 'sev-warn', text: plural(r.errors.length, 'ред има грешка и ще се пропусне', 'реда имат грешка и ще се пропуснат') + ':' }));
      out.appendChild(el('ul', { class: 'imp-errors' }, ...r.errors.slice(0, 200).map((e) => el('li', { text: 'Ред ' + e.row + ': ' + e.reason + (e.name ? ' („' + e.name + '“)' : '') }))));
    }
    if ((r.skipped || []).length) out.appendChild(el('p', { class: 'muted', text: plural(r.skipped.length, 'артикул вече има', 'артикула вече имат') + ' движения — наличността ' + (r.skipped.length === 1 ? 'му' : 'им') + ' не се променя.' }));
    (r.warnings || []).forEach((w) => out.appendChild(el('p', { class: 'muted', text: w })));
    if (done) {
      if (r.stock_on && t.with_stock) foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Виж в Склад', onClick: () => { closeModal(); state().view = 'warehouse'; window.CRM.renderShell(); } }));
      foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: () => { closeModal(); reload(); } }));
      return;
    }
    foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
    // Колко реда от файла ще оставят следа: нови, обновени или с наличност.
    const n = new Set([].concat(r.create || [], r.update || [], r.openings || []).map((x) => x.row)).size;
    if (n > 0) {
      const go = el('button', { class: 'btn', type: 'button', text: 'Внеси ' + plural(n, 'ред', 'реда'), onClick: async () => {
        go.disabled = true;
        let done2;
        try { done2 = await uploadRaw(url('apply'), file, { contentType: 'application/octet-stream' }); }
        catch (e) { go.disabled = false; toast(e.message, true); return; }
        renderPreview(done2, true);
      } });
      foot.appendChild(go);
    }
  }
  date.addEventListener('change', () => { if (file && date.dataset.iso) templatePreview(); });

  const pickLabel = el('label', { class: 'btn', for: fileInput.id, tabindex: '0', text: 'Изберете попълнената таблица',
    onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); } } });
  const body = el('div', { class: 'imp-dialog' },
    el('ol', { class: 'imp-steps' },
      el('li', {}, el('a', { href: '/api/articles/template' + cq(), download: 'Образец-артикули-и-наличности.xlsx', text: 'Свали образец' }),
        el('span', { class: 'muted', text: ' — таблица за попълване в Excel: по един ред на артикул.' })),
      el('li', {}, pickLabel, fileInput, ' ', fileName),
      stockOn ? el('li', {}, field('Наличностите са към дата', date, { size: 's' }), el('div', { class: 'cal-hint', text: 'Сложете дата ПРЕДИ първия месец, който ще изписвате (напр. 31.08, ако изписвате от септември) — иначе изписването за по-ранен месец не вижда наличността.' })) : null),
    out);
  openModal({ title: 'Внос от Excel', body, foot });
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
}

// Справката от номенклатурата на Конто (ПРОМТ-10в) — същото като досега,
// само в общия прозорец. Сметката се предлага от името на файла.
const accountFromName = (name) => { const m = /(^|[^\d])(\d{3,4})(?!\d)/.exec(String(name || '')); return m ? m[2] : ''; };
const cellKey = (v) => String(v == null ? '' : v).trim().toLowerCase().replace(/\s+/g, ' ');
function excelRow(r) {
  const pick = (keys) => {
    for (const k of keys) {
      const hit = Object.keys(r || {}).find((x) => cellKey(x) === cellKey(k));
      if (hit && String(r[hit]).trim() !== '') return String(r[hit]).trim();
    }
    return '';
  };
  const measure = pick(['Мярка', 'Мярка код']);
  const digits = /^\d+$/.test(measure);
  return {
    code: pick(['Код']), name: pick(['Наименование', 'Име']),
    measure_code: digits ? measure : '', measure_name: pick(['Наименование Мярка', 'Мярка име']) || (digits ? '' : measure),
    account: pick(['Сметка']),
  };
}

async function kontoImport(f, out, foot) {
  let rows;
  try {
    const buf = await f.arrayBuffer();
    const wb = await withXlsx(() => XLSX.read(buf, { type: 'array' }));
    // raw:false — кодът остава текст „001", не числото 1.
    const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '', raw: false });
    rows = data.map(excelRow).filter((r) => r.code || r.name);
  } catch (e) { toast('Грешка при четене на файла.', true); return; }
  if (!rows.length) { toast('Файлът е празен.', true); return; }
  clear(out); clear(foot);
  const account = input({ type: 'text', inputmode: 'numeric', maxlength: '4', placeholder: 'напр. 302', value: accountFromName(f.name) });
  const res = el('div');
  out.appendChild(el('p', { text: 'Файл от номенклатурата на Конто: „' + f.name + '" — ' + plural(rows.length, 'ред', 'реда') + '. Сметката е от името на файла — проверете я.' }));
  out.appendChild(row(field('Сметка', account, { size: 's' })));
  out.appendChild(res);
  const applyBtn = el('button', { class: 'btn', type: 'button', text: 'Внеси', disabled: true, onClick: () => run('apply') });
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(applyBtn);
  const list = (title, items, fmt) => items.length ? el('details', {}, el('summary', { text: title + ' (' + items.length + ')' }),
    el('ul', {}, ...items.slice(0, 300).map((x) => el('li', { text: fmt(x) })))) : null;
  async function run(mode) {
    const acc = account.value.trim();
    if (!/^\d{3,4}$/.test(acc) && !rows.some((r) => r.account)) { toast('Въведете сметката (напр. 302 или 304).', true); account.focus(); return; }
    let r;
    try { r = await api('/articles/import', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, mode, account: acc, rows }) }); }
    catch (e) { toast(e.message, true); return; }
    if (mode === 'apply') {
      closeModal();
      toast('Внесени: ' + (r.created || 0) + ' нови, ' + (r.updated || 0) + ' обновени.');
      reload();
      return;
    }
    clear(res);
    res.appendChild(el('p', { text: 'Нови: ' + r.create.length + ' · Обновени: ' + r.update.length + ' · Без промяна: ' + r.unchanged + (r.errors.length ? ' · С грешка: ' + r.errors.length : '') }));
    if (r.new_groups && r.new_groups.length) res.appendChild(el('p', { text: 'Ще бъде създадена група: ' + r.new_groups.join(', ') + '.' }));
    [
      list('Нови артикули', r.create, (x) => x.code + ' ' + x.name + ' (' + x.measure + ')'),
      list('Обновени', r.update, (x) => x.code + ' ' + x.name + ' — ' + Object.keys(x.changes).map((k) => (x.changes[k].from || '—') + ' → ' + x.changes[k].to).join('; ') + (x.by_name ? ' (намерен по име)' : '')),
      list('Още мерки на същия код — не се внасят (добавете ги като преобразуване в артикула)', r.extra, (x) => x.code + ' ' + x.name + ': ' + x.measure_name + (x.measure_code ? ' (' + x.measure_code + ')' : '')),
      list('Предупреждения', r.warnings || [], (x) => x.code + ' ' + x.name + ': ' + x.text),
      list('Редове с грешка — пропускат се', r.errors, (x) => 'Ред ' + x.row + (x.code ? ' (' + x.code + ')' : '') + ': ' + x.reason),
    ].forEach((n) => { if (n) res.appendChild(n); });
    applyBtn.disabled = !(r.create.length || r.update.length);
  }
  account.addEventListener('change', () => run('preview'));
  run('preview');
}

async function groupsDialog() {
  const box = el('div', {});
  async function drawGroups() {
    clear(box);
    let groups = [];
    try { groups = await api('/article-groups' + cq()); if (!Array.isArray(groups)) groups = []; }
    catch (e) { box.appendChild(loadError(drawGroups, 'Групите не можаха да се заредят.')); return; }
    box.appendChild(el('button', { class: 'btn sm', type: 'button', text: '+ Нова група', style: 'margin-bottom:10px', onClick: () => groupForm(null, drawGroups) }));
    if (!groups.length) { box.appendChild(el('p', { style: 'color:var(--muted)', text: 'Още няма групи. Без група артикулът взима сметките по своя тип.' })); return; }
    const tb = el('tbody');
    groups.slice().sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), 'bg')).forEach((g) => {
      const tr = clickableRow(() => groupForm(g, drawGroups), 'Отвори група ' + (g.name || ''),
        el('td', { style: 'font-weight:600' }, g.name || ''),
        el('td', {}, g.stock_account || '—'), el('td', {}, g.revenue_account || '—'), el('td', {}, g.cogs_account || '—'),
        el('td', { class: 'num' }, g.konto_article_type != null ? String(g.konto_article_type) : '—'));
      if (g.active === false) tr.style.opacity = '0.55';
      tb.appendChild(tr);
    });
    box.appendChild(el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Група' }), el('th', { text: 'Склад' }), el('th', { text: 'Приход' }), el('th', { text: 'Себестойност' }), el('th', { class: 'num', text: 'Тип за Конто' }))),
      tb));
  }
  const foot = el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: () => { closeModal(); reload(); } }));
  // onDismiss: и при Esc/„×" списъкът с артикули се опреснява (имената на
  // групите в таблицата може да са се сменили).
  openModal({ title: 'Групи артикули', body: box, foot, onDismiss: () => setTimeout(reload, 0) });
  await drawGroups();
}

function groupForm(existing, after) {
  const g = existing || {};
  const f = {
    name: input({ value: g.name || '', maxlength: '120' }),
    stock_account: input({ value: g.stock_account || '', maxlength: '20', inputmode: 'numeric' }),
    revenue_account: input({ value: g.revenue_account || '', maxlength: '20', inputmode: 'numeric' }),
    cogs_account: input({ value: g.cogs_account || '', maxlength: '20', inputmode: 'numeric' }),
    konto_article_type: input({ type: 'number', step: '1', value: g.konto_article_type != null ? g.konto_article_type : kontoCfg.DEFAULTS.article_type }),
  };
  const body = el('div', {},
    field('Име на групата', f.name),
    row(field('Сметка склад', f.stock_account, { size: 'm' }), field('Сметка приход', f.revenue_account, { size: 'm' }), field('Сметка себестойност', f.cogs_account, { size: 'm' })),
    field('Тип артикул за Конто', f.konto_article_type, { size: 's' }),
    el('p', { style: 'margin:4px 0 0;font-size:var(--fs-sm);color:var(--muted)', text: 'Празна сметка = по типа на артикула (напр. стока: склад 304, приход 702).' }));
  const save = async () => {
    const p = {
      company_id: state().activeCompany,
      name: f.name.value.trim(),
      stock_account: f.stock_account.value.trim() || null,
      revenue_account: f.revenue_account.value.trim() || null,
      cogs_account: f.cogs_account.value.trim() || null,
      konto_article_type: f.konto_article_type.value === '' ? null : Number(f.konto_article_type.value),
    };
    if (!p.name) { toast('Въведете име на групата.', true); f.name.focus(); return; }
    try {
      if (existing && existing.id) await api('/article-groups/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/article-groups', { method: 'POST', body: JSON.stringify(p) });
      toast('Групата е записана.');
      closeModal();
      after();
    } catch (e) { toast(e.message, true); }
  };
  const foot = el('div', {});
  if (existing && existing.id) {
    foot.appendChild(el('button', { class: 'btn sm danger', type: 'button', text: 'Изтрий', style: 'margin-right:auto', onClick: async () => {
      const ok = await confirmDialog('Да изтрия ли групата „' + (g.name || '') + '"?', { okText: 'Изтрий', danger: true });
      if (!ok) return;
      // 409 (в групата има артикули) идва със съобщение какво да се направи —
      // показваме го и формата остава отворена.
      try { await api('/article-groups/' + existing.id, { method: 'DELETE' }); toast('Групата е изтрита.'); closeModal(); after(); }
      catch (e) { toast(e.message, true); }
    } }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: (existing && existing.id) ? 'Редакция на група' : 'Нова група', body, foot });
}
